import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { auth, functions } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import logger from '../utils/logger';
import { mapErrorToUserMessage } from '../utils/userErrorMessage';
import { useAnalytics } from '../hooks/useAnalytics';
import { httpsCallable } from 'firebase/functions';
import { BrandLogo, FaeBillMark } from '../components/common/BrandLogo';
import { setPendingInviteToken } from '../utils/advisorInviteToken';
import { setClientInviteToken } from '../utils/clientInviteToken';
import { setPendingAccountTypeKanzlei } from '../utils/pendingAccountType';

// Konstanten für Dokumentenversionen
const LEGAL_DOCUMENT_VERSIONS = {
  avv: '1.0',
  agb: '1.0', // Wird später aktualisiert wenn PDF vorhanden
  datenschutz: '1.0', // Wird später aktualisiert wenn PDF vorhanden
};

// URLs für rechtliche Dokumente
const LEGAL_DOCUMENT_URLS = {
  avv: 'https://firebasestorage.googleapis.com/v0/b/billution-invoices.firebasestorage.app/o/legal-documents%2FVereinbarung%20zur%20Auftragsverarbeitung%20v1.0.pdf?alt=media',
  agb: '/agb', // Interne Seite
  datenschutz: '/datenschutz', // Interne Seite
};

const RegisterPage: React.FC = () => {
  const { t } = useTranslation('common');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const location = useLocation();
  const { trackSignUp, trackFormSubmission, trackError } = useAnalytics();

  // States für rechtliche Zustimmungen
  const [acceptedAgb, setAcceptedAgb] = useState(false);
  const [acceptedDatenschutz, setAcceptedDatenschutz] = useState(false);
  const [acceptedAvv, setAcceptedAvv] = useState(false);
  
  // Marketing-Consent (optional)
  const [acceptedMarketing, setAcceptedMarketing] = useState(false);

  // M-OB-11 (§8.1): Kanzlei-/Steuerberater-Kennzeichnung. Optional, per Default
  // aus. `?type=kanzlei` belegt sie vor (Query-Read-Muster wie `?invite=`).
  const [isKanzleiSignup, setIsKanzleiSignup] = useState(false);

  const planId = new URLSearchParams(location.search).get('plan');

  // M11b: Capture the ?invite= token into localStorage so it survives the
  // auth redirect chain (register → verify-email → dashboard).
  // Runs once per URL (location.search change) — BEFORE the currentUser
  // redirect below — so even if currentUser is already set and the redirect
  // fires immediately, the token has been persisted first.
  React.useEffect(() => {
    const inviteToken = new URLSearchParams(location.search).get('invite');
    if (inviteToken) {
      setPendingInviteToken(inviteToken);
    }
  }, [location.search]);

  // WL-K5a: Capture the ?clientInvite= token (Kanzlei→Mandant reverse invite) into
  // its OWN localStorage key so it survives the auth redirect chain. Separate key
  // from the advisor token (B-E5) — never routed through the advisor-invite path.
  React.useEffect(() => {
    const clientInviteToken = new URLSearchParams(location.search).get('clientInvite');
    if (clientInviteToken) {
      setClientInviteToken(clientInviteToken);
    }
  }, [location.search]);

  // M-OB-11 (§8.1): `?type=kanzlei` belegt den Toggle vor (Query-Read-Muster
  // wie `?invite=`). Läuft einmal pro URL — reine Vorbelegung, der Nutzer kann
  // die Wahl weiterhin ändern.
  React.useEffect(() => {
    if (new URLSearchParams(location.search).get('type') === 'kanzlei') {
      setIsKanzleiSignup(true);
    }
  }, [location.search]);

  React.useEffect(() => {
    if (currentUser && !loading) {
      if (currentUser.emailVerified) {
          navigate('/dashboard');
      } else {
          navigate('/verify-email', { state: { email: currentUser.email, plan: planId } });
      }
    }
  }, [currentUser, navigate, planId, loading]);

  // Prüfe ob alle rechtlichen Vereinbarungen akzeptiert wurden
  const allAgreementsAccepted = acceptedAgb && acceptedDatenschutz && acceptedAvv;

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validierung: Rechtliche Zustimmungen
    if (!allAgreementsAccepted) {
      setError('Bitte stimmen Sie allen rechtlichen Vereinbarungen zu, um fortzufahren.');
      trackError('Legal agreements not accepted');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Die Passwörter stimmen nicht überein.');
      trackError('Password mismatch in registration form');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      if (userCredential.user) {
          // E-Mail Verifizierung sofort senden, bevor andere asynchrone Operationen starten
          try {
            await sendEmailVerification(userCredential.user);
          } catch (emailError) {
            logger.error(emailError, 'Error sending initial verification email');
            // Wir setzen den Prozess fort, auch wenn die E-Mail fehlschlägt. 
            // Der Benutzer kann sie auf der Verify-Seite erneut anfordern.
          }

          // Rechtliche Zustimmungen mit IP-Adresse speichern
          try {
            const idToken = await userCredential.user.getIdToken();
            
            // HTTP-Endpoint aufrufen um echte IP zu erfassen
            const response = await fetch(
              `https://europe-west3-billution-invoices.cloudfunctions.net/logLegalAgreementsWithIp`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  idToken,
                  agreements: {
                    avv: acceptedAvv,
                    agb: acceptedAgb,
                    datenschutz: acceptedDatenschutz,
                  },
                  versions: LEGAL_DOCUMENT_VERSIONS,
                  // Marketing-Consent: "full" wenn Checkbox gesetzt, sonst "functions" (Standard)
                  marketingConsent: acceptedMarketing ? 'full' : 'functions',
                }),
              }
            );
            
            if (!response.ok) {
              // Fallback: Callable Function verwenden wenn HTTP fehlschlägt
              logger.warn('HTTP endpoint failed, trying callable function');
              const logAgreements = httpsCallable(functions, 'logLegalAgreements');
              await logAgreements({
                agreements: {
                  avv: acceptedAvv,
                  agb: acceptedAgb,
                  datenschutz: acceptedDatenschutz,
                },
                versions: LEGAL_DOCUMENT_VERSIONS,
                marketingConsent: acceptedMarketing ? 'full' : 'functions',
              });
            }
            
            logger.info('Legal agreements logged successfully');
          } catch (agreementError) {
            // Log aber nicht blockieren - Registrierung trotzdem fortsetzen
            logger.error(agreementError, 'Error logging legal agreements');
          }
          
          // M-OB-11 (§8.1): Kanzlei-Kennzeichnung als localStorage-Marker
          // persistieren, sofern gewählt. `AuthContext` schreibt daraus nach dem
          // ersten sellerData-Load `accountType='kanzlei'`. Nur im Erfolgspfad —
          // kein stale Marker bei fehlgeschlagener Registrierung.
          if (isKanzleiSignup) {
            setPendingAccountTypeKanzlei();
          }

          // Analytics: Erfolgreiche Registrierung als GA4-Standard `sign_up` tracken
          trackSignUp({ method: 'email' });
          trackFormSubmission('registration_form', true);

          navigate('/verify-email', { state: { email: userCredential.user.email, plan: planId, acceptedMarketing } });
      } else {
         throw new Error("Benutzer konnte nicht erstellt werden.");
      }
    } catch (err: any) {
      logger.error(err, "registration error");
      
      // Analytics: Registrierungsfehler tracken
      trackFormSubmission('registration_form', false);
      trackError(`Registration failed: ${err.code || 'unknown'}`);
      
      setError(mapErrorToUserMessage(err, t));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center auth-bg py-12 px-4 sm:px-6 lg:px-8">
      <div className="absolute top-4 left-4">
        <a href={process.env.REACT_APP_MARKETING_DOMAIN || 'https://faebill.com'} className="flex items-center">
          <BrandLogo className="h-10" />
        </a>
      </div>
      
      <div className="bg-surface-raised p-8 rounded-lg shadow-xl w-full max-w-md border border-hairline">
        <div className="flex justify-center mb-6">
          <FaeBillMark className="h-12 w-12" />
        </div>
        <h2 className="text-2xl font-bold mb-6 text-center text-primary">Kostenloses Konto erstellen</h2>
        <form onSubmit={handleRegister}>
          {error && (
            <div className="mb-4 bg-[var(--tone-danger-bg)] border-l-4 border-[var(--tone-danger-border)] p-4">
              <p className="text-primary text-sm">{error}</p>
            </div>
          )}
          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium text-secondary mb-1">E-Mail</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2 border border-strong bg-surface-inset text-primary rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="ihre@email.de"
            />
          </div>
          <div className="mb-4">
            <label htmlFor="password" className="block text-sm font-medium text-secondary mb-1">Passwort</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-3 py-2 border border-strong bg-surface-inset text-primary rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Mindestens 6 Zeichen"
            />
          </div>
          <div className="mb-4">
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-secondary mb-1">Passwort bestätigen</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="w-full px-3 py-2 border border-strong bg-surface-inset text-primary rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Passwort wiederholen"
            />
          </div>

          {/* M-OB-11 (§8.1): Kanzlei-/Steuerberater-Kennzeichnung — unter den
              Feldern, VOR den rechtlichen Vereinbarungen. Optional, per Default
              aus. Hilfetext bewusst als <span> (kein <p>), damit die statische
              Paragraphen-Struktur des Formulars unverändert bleibt. */}
          <div className="mb-4 p-4 bg-[rgb(var(--surface-inset-rgb)/0.5)] rounded-lg border border-strong">
            <div className="flex items-start">
              <input
                type="checkbox"
                id="isKanzlei"
                checked={isKanzleiSignup}
                onChange={(e) => setIsKanzleiSignup(e.target.checked)}
                className="mt-1 h-4 w-4 text-indigo-600 border-gray-500 rounded focus:ring-indigo-500 bg-surface-hover"
              />
              <label htmlFor="isKanzlei" className="ml-2 text-sm font-medium text-secondary">
                {t('register.kanzleiToggle.label')}
              </label>
            </div>
            <span className="mt-2 block text-xs text-muted">
              {t('register.kanzleiToggle.help')}
            </span>
          </div>

          {/* Rechtliche Vereinbarungen */}
          <div className="mb-6 space-y-3 p-4 bg-[rgb(var(--surface-inset-rgb)/0.5)] rounded-lg border border-strong">
            <p className="text-sm font-medium text-secondary mb-3">
              Für die Nutzung von FaeBill benötigen wir Ihre Zustimmung zu folgenden Vereinbarungen:
            </p>
            
            {/* AGB Checkbox */}
            <div className="flex items-start">
              <input
                type="checkbox"
                id="acceptAgb"
                checked={acceptedAgb}
                onChange={(e) => setAcceptedAgb(e.target.checked)}
                className="mt-1 h-4 w-4 text-indigo-600 border-gray-500 rounded focus:ring-indigo-500 bg-surface-hover"
              />
              <label htmlFor="acceptAgb" className="ml-2 text-sm text-secondary">
                Ich akzeptiere die{' '}
                <a
                  href={LEGAL_DOCUMENT_URLS.agb}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent-ink hover:text-accent-ink underline"
                >
                  Allgemeinen Geschäftsbedingungen (AGB)
                </a>
                {' '}*
              </label>
            </div>

            {/* Datenschutz Checkbox */}
            <div className="flex items-start">
              <input
                type="checkbox"
                id="acceptDatenschutz"
                checked={acceptedDatenschutz}
                onChange={(e) => setAcceptedDatenschutz(e.target.checked)}
                className="mt-1 h-4 w-4 text-indigo-600 border-gray-500 rounded focus:ring-indigo-500 bg-surface-hover"
              />
              <label htmlFor="acceptDatenschutz" className="ml-2 text-sm text-secondary">
                Ich habe die{' '}
                <a
                  href={LEGAL_DOCUMENT_URLS.datenschutz}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent-ink hover:text-accent-ink underline"
                >
                  Datenschutzerklärung
                </a>
                {' '}gelesen und stimme der Verarbeitung meiner Daten zu *
              </label>
            </div>

            {/* AVV Checkbox */}
            <div className="flex items-start">
              <input
                type="checkbox"
                id="acceptAvv"
                checked={acceptedAvv}
                onChange={(e) => setAcceptedAvv(e.target.checked)}
                className="mt-1 h-4 w-4 text-indigo-600 border-gray-500 rounded focus:ring-indigo-500 bg-surface-hover"
              />
              <label htmlFor="acceptAvv" className="ml-2 text-sm text-secondary">
                Ich akzeptiere die{' '}
                <a
                  href={LEGAL_DOCUMENT_URLS.avv}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent-ink hover:text-accent-ink underline"
                >
                  Vereinbarung zur Auftragsverarbeitung (AVV)
                </a>
                {' '}*
              </label>
            </div>

            {/* Marketing-Einwilligung (optional) */}
            <div className="flex items-start">
              <input
                type="checkbox"
                id="acceptMarketing"
                checked={acceptedMarketing}
                onChange={(e) => setAcceptedMarketing(e.target.checked)}
                className="mt-1 h-4 w-4 text-indigo-600 border-gray-500 rounded focus:ring-indigo-500 bg-surface-hover"
              />
              <label htmlFor="acceptMarketing" className="ml-2 text-sm text-secondary">
                Ich möchte über neue Features, Tipps und exklusive Angebote per E-Mail informiert werden
              </label>
            </div>

            <p className="text-xs text-muted mt-2">
              * Pflichtfeld – Diese Zustimmungen werden rechtssicher mit Zeitstempel und IP-Adresse protokolliert.
            </p>
          </div>

          <button
            type="submit"
            disabled={loading || !allAgreementsAccepted}
            className="w-full bg-indigo-600 text-[#0C1322] py-2 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Erstelle Konto...' : 'Konto erstellen'}
          </button>
        </form>
        <p className="mt-6 text-center text-sm text-secondary">
          Bereits ein Konto?{' '}
          <Link to="/login" className="font-medium text-accent-ink hover:text-accent-ink transition-colors">
            Zum Login
          </Link>
        </p>
        <p className="mt-2 text-center text-sm">
          <a href={process.env.REACT_APP_MARKETING_DOMAIN || 'https://faebill.com'} className="text-muted hover:text-secondary transition-colors">
            Zurück zur Startseite
          </a>
        </p>
      </div>
    </div>
  );
};

export default RegisterPage; 